import React, { useState } from 'react';
import { View, Text, Pressable, TextInput, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { Comment, CommentReactionType } from '../../shared/types/comments';
import { useCommentStore } from '../../store/commentStore';
import { useAuthStore } from '../../store/authStore';
import { REACTION_EMOJIS, getReactionLabel } from '../../shared/config/reactions';
import { decodeHtmlEntities } from '../../shared/utils/html-entities';
import { addColorOpacity } from '../../shared/utils/styles';
import { getDisplayName, getInitials } from '../../shared/utils/user';
import { Ionicons } from '@expo/vector-icons';
import { logger } from '../../shared/utils/logger';

interface ChatMessageProps {
  comment: Comment;
  onReply?: (comment: Comment) => void;
  showActions?: boolean;
}

export function ChatMessage({ comment, onReply, showActions = true }: ChatMessageProps) {
  const styles = useStyles();
  const { isMobile, isSmallMobile } = useResponsive();
  const user = useAuthStore((state) => state.user);
  const { addReaction, removeReaction, deleteComment, updateComment } = useCommentStore();

  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isOwnMessage = user?.id === comment.created_by;

  // Format relative time
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const displayName = getDisplayName(comment.created_by_user);
  const initials = getInitials(comment.created_by_user);

  const handleReaction = async (reactionType: CommentReactionType) => {
    try {
      const hasReaction = comment.reactions?.some(
        r => r.user_id === user?.id && r.reaction_type === reactionType
      );

      if (hasReaction) {
        await removeReaction(comment.id, reactionType);
      } else {
        await addReaction(comment.id, reactionType);
      }
      setShowReactionPicker(false);
    } catch (error) {
      logger.error('Error toggling reaction', error, 'chat');
    }
  };

  const handleDelete = async () => {
    if (!isOwnMessage) return;
    setIsDeleting(true);
    try {
      await deleteComment(comment.id);
    } catch (error) {
      logger.error('Error deleting comment', error, 'chat');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveEdit = async () => {
    const trimmed = editContent.trim();
    if (!trimmed) return;
    try {
      await updateComment(comment.id, { content: trimmed });
      setIsEditing(false);
    } catch (error) {
      logger.error('Error updating comment', error, 'chat');
    }
  };

  const handleCancelEdit = () => {
    setEditContent(comment.content);
    setIsEditing(false);
  };

  // Group reactions by type with counts
  const reactionGroups = comment.reactions?.reduce((acc, r) => {
    if (!acc[r.reaction_type]) {
      acc[r.reaction_type] = { count: 0, hasOwn: false };
    }
    acc[r.reaction_type].count++;
    if (r.user_id === user?.id) {
      acc[r.reaction_type].hasOwn = true;
    }
    return acc;
  }, {} as Record<string, { count: number; hasOwn: boolean }>) || {};

  // Responsive sizes
  const avatarSize = isSmallMobile ? 24 : isMobile ? 28 : 32;
  const fontSize = isSmallMobile ? 13 : isMobile ? 14 : 15;
  const smallFontSize = isSmallMobile ? 10 : isMobile ? 11 : 12;
  const spacing = isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm;

  return (
    <View
      style={{
        paddingVertical: spacing,
        paddingHorizontal: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm,
      }}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
    >
      {/* Reply indicator */}
      {comment.parent_comment_id && (
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
          marginBottom: 4,
          marginLeft: avatarSize + spacing,
        }}>
          <Ionicons 
            name="return-up-back" 
            size={smallFontSize} 
            color={addColorOpacity(styles.tokens.color.onSurface, 0.5)} 
          />
          <Text style={{
            fontSize: smallFontSize,
            color: addColorOpacity(styles.tokens.color.onSurface, 0.5),
            fontStyle: 'italic',
          }}>
            Reply
          </Text>
        </View>
      )}

      <View style={{ flexDirection: 'row', gap: spacing }}>
        {/* Avatar */}
        <View style={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: avatarSize / 2,
          backgroundColor: isOwnMessage 
            ? styles.tokens.color.primary 
            : addColorOpacity(styles.tokens.color.secondary, 0.3),
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Text style={{
            fontSize: avatarSize * 0.4,
            fontWeight: '600',
            color: isOwnMessage 
              ? styles.tokens.color.onPrimary 
              : styles.tokens.color.onSurface,
          }}>
            {initials}
          </Text>
        </View>

        {/* Message content */}
        <View style={{ flex: 1, minWidth: 0 }}>
          {/* Header: Name + Time */}
          <View style={{ 
            flexDirection: 'row', 
            alignItems: 'center', 
            gap: styles.utils.spacing.sm,
            marginBottom: 2,
          }}>
            <Text style={{
              fontSize: smallFontSize + 1,
              fontWeight: '600',
              color: styles.tokens.color.onSurface,
            }} numberOfLines={1}>
              {displayName}
            </Text>
            <Text style={{
              fontSize: smallFontSize,
              color: addColorOpacity(styles.tokens.color.onSurface, 0.5),
            }}>
              {formatTime(comment.created_at)}
            </Text>
            {comment.updated_at !== comment.created_at && (
              <Text style={{
                fontSize: smallFontSize - 1,
                color: addColorOpacity(styles.tokens.color.onSurface, 0.4),
                fontStyle: 'italic',
              }}>
                (edited)
              </Text>
            )}
          </View>

          {/* Message bubble */}
          {isEditing ? (
            <View style={{ gap: styles.utils.spacing.sm }}>
              <TextInput
                value={editContent}
                onChangeText={setEditContent}
                multiline
                autoFocus
                spellCheck={true}
                autoCorrect={true}
                style={{
                  backgroundColor: styles.tokens.color.surface,
                  borderWidth: 1,
                  borderColor: styles.tokens.color.primary,
                  borderRadius: styles.utils.radius.md,
                  padding: styles.utils.spacing.sm,
                  fontSize,
                  color: styles.tokens.color.onSurface,
                  minHeight: 60,
                  ...(Platform.OS === 'web' && { outlineWidth: 0 }),
                }}
              />
              <View style={{ flexDirection: 'row', gap: styles.utils.spacing.xs }}>
                <Pressable
                  onPress={handleSaveEdit}
                  style={{
                    paddingHorizontal: styles.utils.spacing.sm,
                    paddingVertical: styles.utils.spacing.xs,
                    backgroundColor: styles.tokens.color.primary,
                    borderRadius: styles.utils.radius.sm,
                  }}
                >
                  <Text style={{ 
                    fontSize: smallFontSize, 
                    color: styles.tokens.color.onPrimary,
                    fontWeight: '600',
                  }}>
                    Save
                  </Text>
                </Pressable>
                <Pressable
                  onPress={handleCancelEdit}
                  style={{
                    paddingHorizontal: styles.utils.spacing.sm,
                    paddingVertical: styles.utils.spacing.xs,
                    backgroundColor: addColorOpacity(styles.tokens.color.onSurface, 0.1),
                    borderRadius: styles.utils.radius.sm,
                  }}
                >
                  <Text style={{ 
                    fontSize: smallFontSize, 
                    color: styles.tokens.color.onSurface,
                  }}>
                    Cancel
                  </Text>
                </Pressable>
              </View>
            </View>
          ) : (
            <View style={{
              backgroundColor: isOwnMessage
                ? addColorOpacity(styles.tokens.color.primary, 0.08)
                : addColorOpacity(styles.tokens.color.onSurface, 0.04),
              borderRadius: styles.utils.radius.md,
              borderTopLeftRadius: isSmallMobile ? styles.utils.radius.sm : styles.utils.radius.md,
              padding: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md,
              maxWidth: isMobile ? '100%' : '85%',
            }}>
              <Text style={{
                fontSize,
                color: styles.tokens.color.onSurface,
                lineHeight: fontSize * 1.4,
              }}>
                {decodeHtmlEntities(comment.content)}
              </Text>
            </View>
          )}

          {/* Reactions */}
          {Object.keys(reactionGroups).length > 0 && !isEditing && (
            <View style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 4,
              marginTop: 4,
            }}>
              {Object.entries(reactionGroups).map(([emoji, data]) => (
                <Pressable
                  key={emoji}
                  onPress={() => handleReaction(emoji as CommentReactionType)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 2,
                    paddingHorizontal: 6,
                    paddingVertical: 2,
                    borderRadius: 12,
                    backgroundColor: data.hasOwn
                      ? addColorOpacity(styles.tokens.color.primary, 0.15)
                      : addColorOpacity(styles.tokens.color.onSurface, 0.08),
                    borderWidth: data.hasOwn ? 1 : 0,
                    borderColor: addColorOpacity(styles.tokens.color.primary, 0.3),
                  }}
                >
                  <Text style={{ fontSize: smallFontSize + 2 }}>{emoji}</Text>
                  <Text style={{
                    fontSize: smallFontSize,
                    color: data.hasOwn 
                      ? styles.tokens.color.primary 
                      : addColorOpacity(styles.tokens.color.onSurface, 0.7),
                    fontWeight: data.hasOwn ? '600' : '400',
                  }}>
                    {data.count}
                  </Text>
                </Pressable>
              ))}
            </View>
          )}

          {/* Actions - show on hover (desktop) or always visible condensed (mobile) */}
          {showActions && !isEditing && (isHovered || isMobile) && (
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: isSmallMobile ? 8 : 12,
              marginTop: 4,
              opacity: isHovered || isMobile ? 1 : 0,
            }}>
              {/* Reaction picker toggle */}
              <Pressable
                onPress={() => setShowReactionPicker(!showReactionPicker)}
                hitSlop={8}
              >
                <Text style={{
                  fontSize: smallFontSize,
                  color: addColorOpacity(styles.tokens.color.onSurface, 0.6),
                }}>
                  React
                </Text>
              </Pressable>

              {/* Reply */}
              {onReply && (
                <Pressable onPress={() => onReply(comment)} hitSlop={8}>
                  <Text style={{
                    fontSize: smallFontSize,
                    color: addColorOpacity(styles.tokens.color.onSurface, 0.6),
                  }}>
                    Reply
                  </Text>
                </Pressable>
              )}

              {/* Edit (own messages only) */}
              {isOwnMessage && (
                <Pressable onPress={() => setIsEditing(true)} hitSlop={8}>
                  <Text style={{
                    fontSize: smallFontSize,
                    color: addColorOpacity(styles.tokens.color.onSurface, 0.6),
                  }}>
                    Edit
                  </Text>
                </Pressable>
              )}

              {/* Delete (own messages only) */}
              {isOwnMessage && (
                <Pressable onPress={handleDelete} disabled={isDeleting} hitSlop={8}>
                  <Text style={{
                    fontSize: smallFontSize,
                    color: isDeleting 
                      ? addColorOpacity(styles.tokens.color.error, 0.5)
                      : styles.tokens.color.error,
                  }}>
                    {isDeleting ? '...' : 'Delete'}
                  </Text>
                </Pressable>
              )}
            </View>
          )}

          {/* Reaction picker */}
          {showReactionPicker && (
            <View style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 4,
              marginTop: 8,
              padding: 8,
              backgroundColor: styles.tokens.color.surface,
              borderRadius: styles.utils.radius.md,
              borderWidth: 1,
              borderColor: styles.tokens.color.border,
              ...(Platform.OS === 'web' && {
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }),
            }}>
              {REACTION_EMOJIS.map(emoji => {
                const hasOwn = reactionGroups[emoji]?.hasOwn;
                return (
                  <Pressable
                    key={emoji}
                    onPress={() => handleReaction(emoji)}
                    style={{
                      padding: 8,
                      borderRadius: styles.utils.radius.sm,
                      backgroundColor: hasOwn
                        ? addColorOpacity(styles.tokens.color.primary, 0.15)
                        : 'transparent',
                    }}
                    accessibilityLabel={getReactionLabel(emoji)}
                  >
                    <Text style={{ fontSize: 20 }}>{emoji}</Text>
                  </Pressable>
                );
              })}
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

